import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  CalendarCheck,
  MapPin,
  StickyNote,
  PenSquare,
  Clock,
  CheckCircle2,
  Circle,
  PauseCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { showToastNotification } from "../../utility/helper";
import { RoutesLink } from "../../utility/RouteLinks";
import { SiteService } from "@/services/Site/Site";
import type { ISite } from "@/interface/site.interface";
import { ThrowError } from "@/api/callApi";


const STATUS_STYLES: Record<
  string,
  { bg: string; text: string; border: string; icon: React.ReactNode }
> = {
  active: {
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    border: "border-emerald-200",
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  completed: {
    bg: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-200",
    icon: <Circle className="h-4 w-4" />,
  },
  "on-hold": {
    bg: "bg-amber-50",
    text: "text-amber-600",
    border: "border-amber-200",
    icon: <PauseCircle className="h-4 w-4" />,
  },
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  completed: "Completed",
  "on-hold": "On Hold",
};

const SiteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<ISite | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      navigate(RoutesLink.site);
      return;
    }
    fetchSiteDetail(id);
  }, [id, navigate]);

  const fetchSiteDetail = async (siteId: string) => {
    setLoading(true);
    try {
      const response = await SiteService.getSiteDetail(siteId);
      if (response?.success) {
        setSite(response.data);
      } else {
        showToastNotification("danger", response?.message || "Site not found");
        navigate(RoutesLink.site);
      }
    } catch (error) {
      ThrowError(error);
      navigate(RoutesLink.site);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading site details...</div>
      </div>
    );
  }

  if (!site) {
    return null; 
  }

  const status =
    STATUS_STYLES[site.status] ?? {
      bg: "bg-gray-50",
      text: "text-gray-500",
      border: "border-gray-200",
      icon: <Circle className="h-4 w-4" />,
    };
  const statusLabel = STATUS_LABELS[site.status] || site.status;

  return (
    <div className="space-y-6">
      {/* Page header — bold black title + gray subtitle, same as "Manage Bulk Attendance" */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-9 w-9 p-0 hover:bg-muted shrink-0"
            onClick={() => navigate(RoutesLink.site)}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
              {site.siteName}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Site Code: {site.siteCode}
            </p>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border shrink-0 ${status.bg} ${status.text} ${status.border}`}
        >
          {status.icon}
          {statusLabel}
        </span>
      </div>

      {/* Details card */}
      <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm">
        <div className="px-6 sm:px-8 py-2">
          <DetailRow icon={<MapPin className="h-4 w-4" />} label="Location">
            <span className="text-slate-800">
              {[site.locationAddress, site.city, site.state]
                .filter(Boolean)
                .join(", ")}
            </span>
          </DetailRow>

          <DetailRow icon={<Calendar className="h-4 w-4" />} label="Start Date">
            <div className="flex flex-wrap items-center gap-3">
              <DatePill
                value={
                  site.startDate
                    ? new Date(site.startDate).toLocaleDateString("en-GB")
                    : "N/A"
                }
              />
              <span className="flex items-center gap-1.5 text-sm text-slate-400">
                <Clock className="h-3.5 w-3.5" />
                Created:{" "}
                {site.createdAt
                  ? new Date(site.createdAt).toLocaleString()
                  : "N/A"}
              </span>
            </div>
          </DetailRow>

          <DetailRow
            icon={<CalendarCheck className="h-4 w-4" />}
            label="Expected End Date"
          >
            <DatePill
              value={
                site.expectedEndDate
                  ? new Date(site.expectedEndDate).toLocaleDateString("en-GB")
                  : "N/A"
              }
            />
          </DetailRow>
        </div>

        {/* Notes */}
        {site.notes && (
          <div className="px-6 sm:px-8 pb-6 pt-2">
            <div className="relative bg-slate-50 rounded-xl pl-6 pr-5 py-4 border-l-4 border-slate-300">
              <div className="flex items-start gap-3">
                <PenSquare className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-slate-800 whitespace-pre-wrap">
                    {site.notes}
                  </p>
                  <p className="flex items-center gap-1.5 text-xs text-slate-400 mt-2">
                    <StickyNote className="h-3.5 w-3.5" />
                    Additional notes
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DetailRow = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center gap-4 py-4 border-b border-slate-100 last:border-b-0">
    <div className="h-7 w-7 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
      {icon}
    </div>
    <span className="text-sm font-semibold text-slate-500 w-40 shrink-0">
      {label}
    </span>
    <div className="text-sm min-w-0">{children}</div>
  </div>
);

const DatePill = ({ value }: { value: string }) => (
  <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-100 text-slate-800 text-sm font-medium">
    {value}
  </span>
);

export default SiteDetail;